import { CUST_REQ_CONTENT_MAX } from '@indipia/shared-features';
import type { CustReqReportItem } from '../../types/custReqReport.types';

// 작업보고 편집·발송 화면의 '요청하신 사항' 순수 로직 — 기획 004 F·F-3, 941 S16-13.
// 근거: 확정결정 29(요청만 선택한 보고는 발송하지 않음)·30(고객용 문장 1000자), K1 3.10(선정 저장 requests[]).

/** 작업보고 응답의 고객 요청 — 선정 값(선택·고객용 문장)은 서버에 저장하지 않고 화면에서만 들고 있다(확정결정 32) */
export type CustReqShareItem = CustReqReportItem;

/** 사용자가 이 화면에서 바꾼 값(요청별 덮어쓰기) */
export interface ReqShareEdit {
  selected: boolean;
  text: string;
}
export type ReqShareOverrides = Readonly<Record<number, ReqShareEdit | undefined>>;

/** 선정 저장 요청의 요청별 항목 (K1 3.10) */
export interface ReqShareInput {
  custReqId: number;
  selected: boolean;
  customerText: string;
}

export const CUST_REQ_SHARE_TEXT_MAX = CUST_REQ_CONTENT_MAX;

export const CUST_REQ_SHARE_MESSAGE = {
  title: '고객 요청',
  titleHint: '반영된 요청만 고객님께 보여요',
  unappliedNote: '미반영 · 고객님께 보이지 않아요',
  onlyRequestsHint: '요청만 선택하면 발송되지 않아요. 보고 내용이나 사진을 함께 선택해 주세요.',
  previewTitle: '✓ 요청하신 사항',
  editText: '문장 다듬기',
  editDone: '완료',
  textLabel: '고객님께 보여줄 문장',
  textResetHint: '비우면 요청 원문으로 돌아가요',
  saveFail: '저장에 실패했습니다. 다시 시도해주세요.',
} as const;

/** 반영된 요청(고객에게 보일 수 있는 후보)과 미반영 요청으로 나눔 — 서버 순서 유지 */
export function splitShareItems<T extends { applied: boolean }>(items: readonly T[] | undefined | null): {
  applied: T[];
  unapplied: T[];
} {
  const list = items ?? [];
  return { applied: list.filter((i) => i.applied), unapplied: list.filter((i) => !i.applied) };
}

/** 요청 기본값 — 켜짐·원문 */
export function defaultReqEdit(item: CustReqShareItem): ReqShareEdit {
  return { selected: true, text: item.content };
}

/** 화면에 쓰는 값 — 덮어쓴 값이 있으면 그 값, 없으면 기본값 */
export function resolveReqEdit(item: CustReqShareItem, overrides: ReqShareOverrides): ReqShareEdit {
  return overrides[item.custReqId] ?? defaultReqEdit(item);
}

/** 고객용 문장 정리 — 비어 있으면 요청 원문 */
export function normalizeText(text: string, item: Pick<CustReqShareItem, 'content'>): string {
  return text.trim() === '' ? item.content : text;
}

/** 길이 검증 — 한도 초과 문구, 정상이면 null (입력창 maxLength 와 같은 한도) */
export function validateShareText(text: string): string | null {
  return text.length > CUST_REQ_SHARE_TEXT_MAX ? `${CUST_REQ_SHARE_TEXT_MAX}자 이하로 입력해 주세요` : null;
}

/** 글자 수 표시 — '12 / 1000' */
export function countLabel(text: string): string {
  return `${text.length} / ${CUST_REQ_SHARE_TEXT_MAX}`;
}

export function withSelected(
  overrides: ReqShareOverrides,
  item: CustReqShareItem,
  selected: boolean,
): ReqShareOverrides {
  return { ...overrides, [item.custReqId]: { ...resolveReqEdit(item, overrides), selected } };
}

export function withText(overrides: ReqShareOverrides, item: CustReqShareItem, text: string): ReqShareOverrides {
  return { ...overrides, [item.custReqId]: { ...resolveReqEdit(item, overrides), text } };
}

/** [작업보고 전체 고객공유하기] — 반영된 요청을 모두 켬(고객용 문장은 유지) */
export function selectAllApplied(
  items: readonly CustReqShareItem[] | undefined | null,
  overrides: ReqShareOverrides,
): ReqShareOverrides {
  let next = overrides;
  for (const it of splitShareItems(items).applied) next = withSelected(next, it, true);
  return next;
}

/** 미리보기·발송 대상 — 반영되어 있고 선택된 요청. text=표시 문장(비면 원문), raw=입력칸 값 */
export function selectedShareEntries(
  items: readonly CustReqShareItem[] | undefined | null,
  overrides: ReqShareOverrides,
): { item: CustReqShareItem; text: string; raw: string }[] {
  const out: { item: CustReqShareItem; text: string; raw: string }[] = [];
  for (const it of splitShareItems(items).applied) {
    const e = resolveReqEdit(it, overrides);
    if (e.selected) out.push({ item: it, text: normalizeText(e.text, it), raw: e.text });
  }
  return out;
}

/** 선정 저장 요청의 requests — 반영된 요청 전부(켜짐·꺼짐 모두). 반영된 요청이 없으면 undefined 라 키 자체를 보내지 않음 */
export function buildRequestsPayload(
  items: readonly CustReqShareItem[] | undefined | null,
  overrides: ReqShareOverrides,
): ReqShareInput[] | undefined {
  const applied = splitShareItems(items).applied;
  if (applied.length === 0) return undefined;
  return applied.map((it) => {
    const e = resolveReqEdit(it, overrides);
    return { custReqId: it.custReqId, selected: e.selected, customerText: normalizeText(e.text, it) };
  });
}

/** 요청만 선택하고 메시지·사진이 없어 발송되지 않는 상태 (확정결정 29) */
export function isRequestOnly(selectedCount: number, hasShareContent: boolean): boolean {
  return selectedCount > 0 && !hasShareContent;
}
